import { CheckCircleIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { formatDate, WorkflowTargetId } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { Header } from "components/header";
import { DeleteButton } from "components/ui/action-button";
import {
    GetWorkflowExecutionsForEntityQuery,
    useCancelWorkflowExecutionMutation,
    useGetWorkflowExecutionsForEntityQuery,
    WorkflowExecutionResult,
} from "generated/graphql";
import React, { useCallback, useEffect } from "react";
import { executionActionLabel } from "./workflow-execution-helper";

interface WorkflowEntity {
    id: WorkflowTargetId;
}

interface WorkflowEntityExecutionsProps {
    entity: WorkflowEntity;
}

export const WorkflowEntityExecutions: React.FC<
    WorkflowEntityExecutionsProps
> = props => {
    const { entity } = props;
    const workflowExecutionsQuery = useGetWorkflowExecutionsForEntityQuery({
        workflowTargetId: entity.id,
    });

    useEffect(() => {
        workflowExecutionsQuery.refetch();
    }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <QueryRenderer name="WorkflowEntityExecutions" query={workflowExecutionsQuery}>
            {data =>
                data.getWorkflowExecutionsForEntity.length === 0 ? null : (
                    <InputFormBlock>
                        <Header iconName="36_CheckBackLater">Workflow automation</Header>
                        <div className="space-y-1 mt-2">
                            {data.getWorkflowExecutionsForEntity.map(execution => (
                                <WorkflowExecution
                                    key={execution.id}
                                    execution={execution}
                                    refetch={workflowExecutionsQuery.refetch}
                                />
                            ))}
                        </div>
                    </InputFormBlock>
                )
            }
        </QueryRenderer>
    );
};

type WorkflowEntityExecution =
    GetWorkflowExecutionsForEntityQuery["getWorkflowExecutionsForEntity"][0];

interface WorkflowExecutionProps {
    execution: WorkflowEntityExecution;
    refetch: () => void;
}

const ExecutionTitle: React.FC<{ execution: WorkflowEntityExecution }> = props => {
    const actionLabel = executionActionLabel(props.execution);
    return (
        <>
            {props.execution.workflow.name}
            {actionLabel ? (
                <span className="text-sm text-gray-500"> &middot; {actionLabel}</span>
            ) : null}
        </>
    );
};

const WorkflowExecution: React.FC<WorkflowExecutionProps> = props => {
    const { execution, refetch } = props;
    const cancelMutation = useCancelWorkflowExecutionMutation();
    const cancel = useCallback(async () => {
        await cancelMutation.mutateAsync({ workflowExecutionId: execution.id });
        refetch();
    }, [cancelMutation, execution.id, refetch]);
    return (
        <WorkflowRunRow
            title={<ExecutionTitle execution={execution} />}
            run={execution}
            onCancel={cancel}
        />
    );
};

export interface WorkflowRunState {
    scheduledAt: string | null;
    cancelledAt: string | null;
    finishedAt: string | null;
    result: WorkflowExecutionResult | null;
}

interface WorkflowRunRowProps {
    title: React.ReactNode;
    run: WorkflowRunState;
    onCancel: () => Promise<void>;
}

export const WorkflowRunRow: React.FC<WorkflowRunRowProps> = props => {
    const { title, run, onCancel } = props;
    if (run.finishedAt) {
        if (run.result === WorkflowExecutionResult.Error) {
            return (
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <XCircleIcon className="h-5 w-5 text-red-300" />
                        <div className="text-red-600">{title}</div>
                    </div>
                    <div className="flex items-center pr-9">
                        <div className="text-sm text-red-500">
                            completed with errors at {formatDate(run.finishedAt)}
                        </div>
                    </div>
                </div>
            );
        }
        if (run.result === WorkflowExecutionResult.Warning) {
            return (
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <XCircleIcon className="h-5 w-5 text-yellow-300" />
                        <div>{title}</div>
                    </div>
                    <div className="flex items-center pr-9">
                        <div className="text-sm text-yellow-500">
                            completed with warnings at {formatDate(run.finishedAt)}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <div>{title}</div>
                </div>
                <div className="flex items-center pr-9">
                    <div className="text-sm text-gray-700">
                        {formatDate(run.finishedAt)}
                    </div>
                </div>
            </div>
        );
    }
    if (run.cancelledAt) {
        return (
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <XCircleIcon className="h-5 w-5 text-gray-300" />
                    <div className="text-gray-400">{title}</div>
                </div>
                <div className="flex items-center pr-9">
                    <div className="text-sm text-gray-400">
                        canceled at {formatDate(run.cancelledAt)}
                    </div>
                </div>
            </div>
        );
    }
    if (run.scheduledAt) {
        return (
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-yellow-500" />
                    <div className="italic">{title}</div>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-700 italic">
                        scheduled to run at {formatDate(run.scheduledAt)}
                    </div>
                    <DeleteButton
                        confirm={{
                            title: "Remove",
                            message:
                                "Are you sure you want to stop this workflow from running?",
                            confirmLabel: "Yes, stop this workflow",
                            cancelLabel: "No, keep it running",
                        }}
                        onClick={onCancel}
                        backgroundColor="bg-gray-200"
                    />
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center pl-7">
                <div>{title}</div>
            </div>
        </div>
    );
};
